import dayjs from "dayjs";
import { TFunction } from "i18next";
import { GroupedD2DOrder } from "./orderGrouping";
import { getD2DRejectStatusLabelKey } from "../../utils/rejectionStatus";

export const getTooltipTextForDownloadButton = (
  orders: GroupedD2DOrder[],
  isFilterApplied: boolean,
  isDownloading: boolean,
  t: TFunction
) => {
  if (orders.length === 0 && !isFilterApplied) return "";
  if (isDownloading) return t("orders:download.disabled.toolTip");
  return t("orders:download.toolTip");
};

const getStatusLabel = (
  status: string,
  t: TFunction,
  order?: GroupedD2DOrder
): string => {
  if (status === "REJECTED") {
    const key =
      getD2DRejectStatusLabelKey({
        status,
        lastChangedBy: order?.lastChangedBy,
        rejectionType: order?.rejectionType,
        rejectionTrigger: order?.rejectionTrigger,
      }) ?? "d2d:status.manualRejected";
    return t(key);
  }

  const statusMap: Record<string, string> = {
    NEW: t("d2d:d2dOrders.status.requested"),
    ACCEPTED: t("d2d:d2dOrders.status.accepted"),
    SHIPPED: t("d2d:d2dOrders.status.shipped"),
    EXPIRED: t("d2d:d2dOrders.status.expired"),
    COMPLETED: t("d2d:d2dOrders.status.completed"),
    CREDITED: t("d2d:d2dOrders.status.credited"),
  };
  return statusMap[status] || status;
};

const formatDateForExport = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  if (!d.isValid()) return "";
  return d.format("DD-MMM-YYYY hh:mm A");
};

const escapeCSVValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const downloadD2DOrdersAsCSV = (
  orders: GroupedD2DOrder[],
  t: TFunction
) => {
  const headers = [
    t("d2d:d2dOrders.table.header.orderNumber"),
    t("d2d:d2dOrders.table.header.dealerReference"),
    t("d2d:d2dOrders.table.header.partNumber"),
    t("d2d:d2dOrders.table.header.requestedQuantity"),
    t("d2d:d2dOrders.table.header.acceptedQuantity"),
    t("d2d:d2dOrders.table.header.sellerAccountId"),
    t("d2d:d2dOrders.table.header.dateRequested"),
    t("d2d:d2dOrders.table.header.trackingNumber"),
    t("d2d:d2dOrders.table.header.status"),
  ];

  const rows = orders.map((order) => [
    escapeCSVValue(order.orderNumber),
    escapeCSVValue(order.dealerReference || "--"),
    escapeCSVValue(order.partNumber),
    escapeCSVValue(order.requestedQuantity),
    escapeCSVValue(order.acceptedQuantity || "--"),
    escapeCSVValue(order.sellerAccountId || "--"),
    escapeCSVValue(formatDateForExport(order.dateRequested)),
    escapeCSVValue(order.trackingNumber || "--"),
    escapeCSVValue(getStatusLabel(order.status, t, order)),
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
  link.href = url;
  link.download = `D2D_Orders_${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
